/**
 * Hook to validate authentication token on app initialization
 *
 * This hook ensures that the localStorage auth state is in sync with the
 * HTTP-only JWT cookie. If the cookie has expired or is missing, it clears
 * the localStorage to prevent showing a "logged in" state when API calls fail.
 *
 * Additionally, it checks for valid auth cookies even when localStorage
 * indicates not authenticated (handles SSO callback scenario where cookie
 * is set but localStorage hasn't been updated yet).
 *
 * @changeHistory
 * - 2026-10-01: Preserved existing Agent IDs when hydrating broker/admin sessions (Codex)
 * - 2025-11-11: Created hook to fix auth state sync issue - Agent Dashboard Fix (Neer Patel)
 * - 2025-11-15: Lint fix - Marked async validation as intentionally unawaited (GitHub Copilot)
 * - 2025-11-20: Lint fixes - Added explicit return types and removed void operator
 *   (GitHub Copilot)
 * - 2025-12-01: Fixed SSO redirect issue - Check for auth cookie even when localStorage
 *   not authenticated (GitHub Copilot)
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/auth.store';
import { authApi } from '../../services/api-client';

/**
 * Hook to validate the current auth state against the server
 * Returns loading state while validation is in progress
 *
 * @returns Validation state for auth guard components
 *
 * @changeHistory
 * - 2025-11-11: Initial implementation - Added auth validation hook (Neer Patel)
 * - 2025-11-20: Lint fixes - Added explicit return types and validation handler typing (GitHub Copilot)
 * - 2025-12-01: Fixed SSO redirect - Always check server for auth state (GitHub Copilot)
 */
type UseAuthValidationResult = {
  isValidating: boolean;
};

export const useAuthValidation = (): UseAuthValidationResult => {
  const [isValidating, setIsValidating] = useState(true);
  const { isAuthenticated, login, logout } = useAuthStore();

  useEffect(() => {
    const validateAuth = async (): Promise<void> => {
      try {
        // Always try to fetch current user from server to check for valid auth cookie
        // This handles SSO callback scenario where cookie exists but localStorage not updated
        const response = await authApi.me();

        if (response.data?.user) {
          // Valid auth cookie found - sync localStorage if needed
          if (!isAuthenticated) {
            // SSO callback scenario: cookie exists but localStorage not synced
            login(
              {
                id: response.data.user.id,
                email: response.data.user.email,
                fullName: response.data.user.fullName ?? response.data.user.email,
                firstName: response.data.user.firstName,
                lastName: response.data.user.lastName,
                role: response.data.user.role,
                phone: response.data.user.phone,
                agentId: response.data.user.agentId,
              },
              null
            );
          }
        }

        setIsValidating(false);
      } catch (error) {
        // No valid auth cookie - clear localStorage if it claims authenticated
        // Only logout if explicit 401 (session expired/invalid)
        // Avoid logging out on network errors or cancellations (status 0)
        if (
          isAuthenticated &&
          error &&
          typeof error === 'object' &&
          'status' in error &&
          (error as { status: number }).status === 401
        ) {
          console.warn('Auth token validation failed (401), logging out:', error);
          logout();
        } else if (
          isAuthenticated &&
          (!error || typeof error !== 'object' || !('status' in error))
        ) {
          // If error shape is unknown, verify via strict 401 check from API client?
          // Actually, API client handles 401 logout internally.
          // So we technically don't need to call logout() here if API client did it.
          // But if API client didn't (e.g. network error), we should NOT logout.
          console.warn('Auth check failed but not confirmed 401, preserving session:', error);
        }
        setIsValidating(false);
      }
    };

    validateAuth().catch(() => {
      setIsValidating(false);
    });
  }, [isAuthenticated, login, logout]);

  return { isValidating };
};
